import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import Link from 'next/link';
import cx from 'classnames';
import PropTypes from 'prop-types';

import { AlertModal } from '@components';
import { useTheme } from '@context/ThemeProvider';
import { useUserContext } from '@context/UserContent';
import { useSettingContext } from '@context/SettingContent';
import { loadState, saveState } from '@helpers/common';
import { resolveTenantKey } from '@helpers/theme-values-provider';

import { NAV_SECTIONS } from './nav';
import { NavIcon, ChevronIcon, LogoutIcon } from './icons';
import sty from './sidebar.module.scss';

const COLLAPSE_KEY = 'pl.sidebar.collapsed';

// Compact brand mark shown when collapsed (the wordmark logo is hidden then).
// Falls back to "PL" for the default Payment Loyalty tenant.
const TENANT_MARK = { lopay: 'LP', handepay: 'HP' };

function isItemActive(item, pathname) {
  return item.match === 'startsWith' ? pathname.startsWith(item.href) : pathname === item.href;
}

// Merchant identity for the user card: prefer the captured business name,
// fall back to the login email, then a neutral default. Avatar initials come
// from whichever label we show.
function deriveIdentity(businessName, email) {
  const biz = (businessName || '').trim();
  if (biz) {
    const words = biz.split(/\s+/).filter(Boolean);
    const initials =
      words.length > 1 ? words[0][0] + words[1][0] : biz.replace(/[^a-zA-Z0-9]/g, '').slice(0, 2);
    return { name: biz, initials: (initials || 'PL').toUpperCase() };
  }
  if (email) {
    const local = email.split('@')[0].replace(/[^a-zA-Z0-9]/g, '');
    return { name: email, initials: (local.slice(0, 2) || 'PL').toUpperCase() };
  }
  return { name: 'Account', initials: 'PL' };
}

function NavItem({ item, pathname, collapsed }) {
  const active = isItemActive(item, pathname);
  return (
    <li
      className={cx(sty.navItem, { [sty.active]: active })}
      title={collapsed ? item.label : undefined}
    >
      <Link href={item.href} legacyBehavior>
        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
        <a className={sty.navLink}>
          <NavIcon name={item.icon} className={sty.navIcon} />
          <span className={sty.navName}>{item.label}</span>
        </a>
      </Link>
    </li>
  );
}

NavItem.propTypes = {
  item: PropTypes.object.isRequired,
  pathname: PropTypes.string.isRequired,
  collapsed: PropTypes.bool,
};

// Sub-line under the title: show the login email beneath the business name;
// if there's no business name the email is already the title, so fall back to
// the merchant id.
function subLine(businessName, email, merchantId) {
  if (businessName && email) return email;
  if (merchantId != null) return `Merchant ID · ${merchantId}`;
  return '';
}

function UserCard({ businessName, email, merchantId, collapsed, onLogout }) {
  const { name, initials } = deriveIdentity(businessName, email);
  const meta = subLine(businessName, email, merchantId);
  return (
    <div className={sty.user}>
      <div className={sty.userCard} title={collapsed ? name : undefined}>
        <span className={sty.avatar}>{initials}</span>
        <div className={sty.userInfo}>
          <div className={sty.userName}>{name}</div>
          {meta && <div className={sty.userMeta}>{meta}</div>}
        </div>
        <button
          type="button"
          className={sty.logoutBtn}
          onClick={onLogout}
          aria-label="Log out"
          title="Log out"
        >
          <LogoutIcon />
        </button>
      </div>
    </div>
  );
}

UserCard.propTypes = {
  businessName: PropTypes.string,
  email: PropTypes.string,
  merchantId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  collapsed: PropTypes.bool,
  onLogout: PropTypes.func.isRequired,
};

function Sidebar({ handleLogout, nav = NAV_SECTIONS }) {
  const { pathname } = useRouter();
  const { clientConfig, client } = useTheme();
  const { user } = useUserContext();
  const { setting } = useSettingContext() || {};

  const [collapsed, setCollapsed] = useState(false);
  // Gate the width animation until after mount so a restored-collapsed state
  // doesn't slide open→closed on the first paint (SSR renders expanded).
  const [ready, setReady] = useState(false);
  const [logoutModal, setLogoutModal] = useState(false);

  useEffect(() => {
    if (loadState(COLLAPSE_KEY) === true) setCollapsed(true);
    setReady(true);
  }, []);

  function toggleCollapsed() {
    setCollapsed(prev => {
      const next = !prev;
      saveState(COLLAPSE_KEY, next);
      return next;
    });
  }

  const mark = TENANT_MARK[resolveTenantKey(client)] || 'PL';

  return (
    <aside className={cx(sty.sidebar, { [sty.collapsed]: collapsed, [sty.noTransition]: !ready })}>
      <button
        type="button"
        className={sty.collapseBtn}
        onClick={toggleCollapsed}
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        aria-expanded={!collapsed}
        title={collapsed ? 'Expand' : 'Collapse'}
      >
        <ChevronIcon />
      </button>

      <div className={sty.brand}>
        <span className={sty.brandLogo} style={{ backgroundColor: clientConfig?.logoBg }}>
          <img src={clientConfig?.logo} alt="Logo" />
        </span>
        <span className={sty.brandMark}>{mark}</span>
      </div>

      <nav className={sty.scroll}>
        {nav.map(section => (
          <div key={section.label} className={sty.section}>
            <div className={sty.sectionLabel}>{section.label}</div>
            <ul className={sty.nav}>
              {section.items.map(item => (
                <NavItem key={item.id} item={item} pathname={pathname} collapsed={collapsed} />
              ))}
            </ul>
          </div>
        ))}
      </nav>

      <UserCard
        businessName={setting?.businessName}
        email={user?.email}
        merchantId={user?.merchantId}
        collapsed={collapsed}
        onLogout={() => setLogoutModal(true)}
      />

      {logoutModal && (
        <AlertModal
          type="2"
          msg="Are you sure you want to log out?"
          closeFn={() => setLogoutModal(false)}
          closeTxt="No"
          saveFn={handleLogout}
          saveTxt="Yes"
        />
      )}
    </aside>
  );
}

Sidebar.propTypes = {
  handleLogout: PropTypes.func.isRequired,
  nav: PropTypes.array,
};

export default Sidebar;
